import { initializeApp } from 'firebase/app';
import { fetchAndActivate, getRemoteConfig } from 'firebase/remote-config';

const configurations = {
  production: {
    apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
    authDomain: 'tempmeedemo.firebaseapp.com',
    databaseURL: 'https://tempmeedemo.firebaseio.com',
    projectId: 'tempmeedemo',
    storageBucket: 'tempmeedemo.appspot.com',
    messagingSenderId: '925251549185',
    appId: '1:925251549185:web:a22193c7f14317c95be800',
    measurementId: 'G-XLRGQRVNR7',
  },
  staging: {
    apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
    authDomain: 'tempmeestaging.firebaseapp.com',
    projectId: 'tempmeestaging',
    storageBucket: 'tempmeestaging.firebasestorage.app',
    messagingSenderId: '1072442773294',
    appId: '1:1072442773294:web:7faf7e29f85e5e22f9f4bf',
    measurementId: 'G-ZYKCWPCFG5',
  },
  development: {
    apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
    authDomain: 'tempmeedev.firebaseapp.com',
    databaseURL: 'https://tempmeedev.firebaseio.com',
    projectId: 'tempmeedev',
    storageBucket: 'tempmeedev.appspot.com',
    messagingSenderId: '836175226934',
    appId: '1:836175226934:web:5211ef5ffc3211bd909097',
    measurementId: 'G-QDV0ZKYKRM',
  },
};

// Get environment from process.env or default to 'development'
const environment = process.env.REACT_APP_TEMP_MEE_ENV || 'development';

// Select configuration strategy based on environment
const getFirebaseConfig = () => {
  const config = configurations[environment];

  if (!config) {
    console.warn(`Invalid REACT_APP_TEMP_MEE_ENV: "${environment}". Falling back to development.`);
    return configurations.development;
  }

  return config;
};

// Initialisation is lazy and memoised rather than running at module scope.
//
// At module scope, `initializeApp()` throws when REACT_APP_FIREBASE_APIKEY is
// unset — which made importing anything that transitively reached this file
// (RemoteConfigUtils -> userActions -> most of the app) impossible to load in a
// test or any environment without Firebase credentials. `src/index.js` calls
// `initFirebase()` on boot so production timing is unchanged: the fetch still
// starts as early as it used to.
let client = null;
let initialised = false;

/**
 * Returns the Remote Config client, initialising Firebase on first use.
 * Returns `null` — rather than throwing — when Firebase cannot be configured,
 * so callers degrade to Remote Config defaults instead of taking the app down.
 *
 * @returns {import('firebase/remote-config').RemoteConfig | null}
 */
export const getRemoteConfigClient = () => {
  if (initialised) return client;
  initialised = true;

  try {
    const app = initializeApp(getFirebaseConfig());
    client = getRemoteConfig(app);
    // Fetch and activate Remote Config values.
    fetchAndActivate(client).catch((error) => {
      console.warn(`Remote Config activation failed: ${error?.message}`);
    });
  } catch (error) {
    console.warn(`Firebase init skipped: ${error?.message}`);
    client = null;
  }

  return client;
};

/** Eager entry point, called once from `src/index.js`. */
export const initFirebase = () => getRemoteConfigClient();

/** Test seam — drops the memoised client so a suite can re-initialise. */
export const resetFirebaseForTests = () => {
  client = null;
  initialised = false;
};
